
import { Point } from "./Point";
import { Id_attachment, Id_location } from "./api/Identifiers";
import {  MapFeature } from "./MapFeature";
import { Rectangle } from "./Rectangle";
import { Configuration } from "./Configuration";


interface AttachmentJSON {
    adj_coords: number[];
    adj_geo_ref: any[];
    icon: string;
    id_location: number,
    id: string,
    is_3D_link: boolean;
    is_image: boolean;
    is_link: boolean;
    is_video_link: boolean;
    location: string,
    raw_coords: number[];
    raw_geo_ref: any[];
    raw: any;
    type:string;
    url: string,
	description:string;
}


export class Attachment extends MapFeature {
    private static readonly ICON_3D_LINK: string = `${Configuration.azureStorageAccountUrl}/cmsview/icons/48/360.png`;
    private static readonly ICON_IMAGE: string = `${Configuration.azureStorageAccountUrl}/cmsview/icons/48/camera.png`;
    private static readonly ICON_VIDEO: string = `${Configuration.azureStorageAccountUrl}/cmsview/icons/48/video-tiny.png`;


    // MapFeature required methods
    public get generic_id():string{return this.id.toString();}
    public get map_feature_type(): string { return "attachment"; }

    public get is_3D_link(): boolean {
        return this.url.includes("my.matterport.com");
    }

    public get is_video_link(): boolean {
        let rtnVal = false;
        if (this.url.includes("youtube.com")){
            rtnVal = true;
        }
        //NOTE: youtu.be links do not work!!
        return rtnVal;
    }

    public get is_image(): boolean {
        return (this.url.endsWith(".jpeg") || this.url.endsWith(".png"));
    }

    public get description(): string {
        return this._description;
    }

    public get is_link(): boolean {
        return (this.is_video_link || this.is_3D_link);
    }
    public get raw_coords():Point|null{
        return this._raw_coords;
    }
    public get raw_geo_ref():Rectangle|null{
        return this._raw_geo_ref;
    }

    constructor(readonly id: Id_attachment,
        readonly url: string,
        readonly _raw_coords: Point,
        readonly _raw_geo_ref:Rectangle,
        readonly location: string,
        readonly id_location: Id_location,
        readonly raw: any,
		readonly _description:string) { 
        super();
    }

    public get icon_url(): string {
        if (this.is_3D_link) {
            return Attachment.ICON_3D_LINK;
        }
        else if (this.is_video_link) {
            return Attachment.ICON_VIDEO;
        }
        return Attachment.ICON_IMAGE;
    }

    public get toJSON(): AttachmentJSON {
        return this.as_json;
    }

    public get as_json(): AttachmentJSON {
        const r: AttachmentJSON = {
            raw_geo_ref: this.raw_geo_ref.toArray(),
            adj_geo_ref: this.adj_geo_ref.toArray(),
            adj_coords: this.adj_coords.toJSON(),
            raw_coords: this.raw_coords.toJSON(),
            id: this.id.value,
            id_location: this.id_location.value,
            location: this.location,
            url: this.url,
            icon: this.icon_url,
            is_3D_link: this.is_3D_link,
            is_video_link: this.is_video_link,
            is_image: this.is_image,
            is_link: this.is_link,
            raw: this.raw,
            type:this.map_feature_type,
            description:this._description
        };
        return r;
    }
}

export class AttachmentCollection {
    public static empty(): AttachmentCollection { return new AttachmentCollection(); };

    public static wrap(attachments: Attachment[]) {
        let r: AttachmentCollection = new AttachmentCollection();
        attachments.forEach((a: Attachment) => {
            r.update(a);

        });
        return r;
    }

    public get as_array():Attachment[]{
        return this.attachments;
    }
    public update(attachment: Attachment): AttachmentCollection {
        this._temp_attachments_list = [];
        this._attachments_tree[attachment.id.value] = attachment;
        return this;
    }
    private _temp_attachments_list: Attachment[] = [];
    private _attachments_tree: any = {};
    public extend(lhs: AttachmentCollection): AttachmentCollection {
        if (lhs) {
            lhs.attachments.forEach((a: Attachment) => {
                this.update(a);
            });
        }
        return this;
    }

    public get attachments(): Attachment[] {
        if (this._temp_attachments_list.length == 0) {
            for (const key in this._attachments_tree) {
                let a: Attachment = this._attachments_tree[key];
                this._temp_attachments_list.push(a);
            }
        }

        return this._temp_attachments_list;
    }
    public get count(): number {
        return this.attachments.length;
    }

    public filter(id_location: Id_location): AttachmentCollection {
        let r: AttachmentCollection = new AttachmentCollection();
        this.attachments.forEach((attachment: Attachment) => {
            if (attachment.id_location.equals(id_location)) {
                r.update(attachment);
            }
        });
        return r;
    }


    public get_for_floor(id_location: Id_location): AttachmentCollection {
        let a: Attachment[] = this.attachments.filter((a: Attachment) => {
            return a.id_location === id_location;
        });
        return AttachmentCollection.wrap(a);
    }

    public get as_json_by_floor(): any {
        let r: any = {};
        this.attachments.forEach((attachment: Attachment) => {
            let id_floor = attachment.id_location.toString();
            if (!(id_floor in r)) {
                r[id_floor] = {};
            }
            r[id_floor][attachment.id.toString()] = attachment.toJSON;
        });
        return r;
    }

    public get_attachment(id_attachment: Id_attachment): Attachment {
        let attachment: Attachment = this.attachments.find(att => att.id.value === id_attachment.value);
        if (attachment === undefined) {
            attachment = null;
        }
        return attachment;
    }
}